// src/components/organisms/Modal/Dialog.tsx
import React, { ReactNode, useEffect, useLayoutEffect, useState, Fragment } from 'react';
import { createRoot, Root } from 'react-dom/client';
import * as styles from '@/components/organisms/Modal/Modal.css';
import { Button } from 'components/atoms/Button/Button';

export interface DialogProps {
  /** 다이얼로그에 들어갈 JSX (텍스트, 폼, 리스트 등) */
  children: ReactNode;
  /** 확인 버튼 텍스트 */
  okText?: string;
  /** 취소 버튼 텍스트 (없으면 단일 버튼) */
  cancelText?: string;
  /** 확인 클릭 시 호출 */
  onOk?: () => void;
  /** 취소 클릭 시 호출 */
  onCancel?: () => void;
  /** 모달 컨테이너에 적용할 추가 클래스명 */
  className?: string;
  /** 모달 열림 상태 (외부에서 제어) */
  open?: boolean;
  /** footer 숨김 여부 */
  hideFooter?: boolean;
}

export const Dialog: React.FC<DialogProps> = ({
  children,
  okText = '확인',
  cancelText,
  onOk,
  onCancel,
  className,
  open,
  hideFooter = false,
}) => {
  // 상태 초기화 - open prop의 초기값으로 설정
  const [shouldRender, setShouldRender] = useState(open ?? false);
  const [visible, setVisible] = useState(open ?? false);
  
  // open prop이 제공되면 외부에서 제어, 없으면 내부에서 제어
  const isControlled = open !== undefined;

  useEffect(() => {
    if (!isControlled) {
      // 내부 제어 모드: 마운트 시 애니메이션과 함께 표시
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    }
  }, [isControlled]);

  // 외부에서 제어되는 경우 open prop 변경 시 트랜지션 처리
  useEffect(() => {
    if (isControlled) {
      if (open) {
        // 모달이 열릴 때는 shouldRender를 먼저 설정하고, DOM 렌더링 후 visible을 설정
        setShouldRender(true);
        // DOM이 렌더링된 후 visible을 true로 설정하여 애니메이션 트리거
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setVisible(true);
          });
        });
      } else {
        // 모달이 닫힐 때는 visible만 false로 설정하고, 트랜지션 후 shouldRender를 false로 설정
        setVisible(false);
        setTimeout(() => {
          setShouldRender(false);
        }, 200);
      }
    }
  }, [isControlled, open]);

  const close = (fn?: () => void) => {
    // visible을 false로 설정하여 트랜지션 시작
    setVisible(false);
    
    if (isControlled) {
      // 외부에서 제어되는 경우 트랜지션 후 콜백 호출
      setTimeout(() => {
        fn?.();
      }, 200);
    } else {
      // 내부에서 제어되는 경우 트랜지션 후 콜백 호출
      setTimeout(() => {
        fn?.();
      }, 200);
    }
  };

  // 백드롭 클릭 시 onCancel 호출
  const handleBackdropClick = () => close(onCancel);

  // 모달 내부 클릭은 전파 방지
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // shouldRender가 false일 때는 렌더링하지 않음
  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={[
        styles.backdrop,
        visible && styles.backdropVisible,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleBackdropClick}
    >
      <div
        className={[
          styles.modal,
          visible && styles.modalVisible,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={stop}
      >
        <div className={styles.contentTemplate}>{children}</div>
        {!hideFooter && (
          <div className={styles.footer}>
            {cancelText && (
              <Button
                intent="secondary"
                className="w-full max-w-[280px]"
                onClick={() => close(onCancel)}
              >
                {cancelText}
              </Button>
            )}
            <Button
              className="w-full max-w-[280px]"
              onClick={() => close(onOk)}
            >
              {okText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/** 스크립트에서 호출할 수 있는 유틸 함수들 */
function createDialogContainer() {
  const div = document.createElement('div');
  document.body.appendChild(div);
  return { div, root: createRoot(div) };
}
function destroy(div: HTMLDivElement, root: Root) {
  root.unmount();
  div.remove();
}

export const Modal = {
  /** 단순 얼럿 */
  alert(message: string, okText?: string) {
    const { div, root } = createDialogContainer();
    return new Promise<void>((res) => {
      root.render(
        <Dialog
          okText={okText}
          onOk={() => {
            res();
            destroy(div, root);
          }}
        >
          <div className='text-center whitespace-pre-line leading-relaxed text-gray-900 pt-4'>
            {message.split('\n').map((l, i, arr) => (
              <Fragment key={i}>
                {l}
                {i < arr.length - 1 && <br />}
              </Fragment>
            ))}
          </div>
        </Dialog>
      );
    });
  },

  /** 내용 커스텀 confirm */
  confirm(
    content: React.ReactNode,
    okText?: string,
    cancelText?: string
  ) {
    const { div, root } = createDialogContainer();
    return new Promise<boolean>((res) => {
      root.render(
        <Dialog
          okText={okText}
          cancelText={cancelText}
          onOk={() => {
            res(true);
            destroy(div, root);
          }}
          onCancel={() => {
            res(false);
            destroy(div, root);
          }}
        >
          {content}
        </Dialog>
      );
    });
  },
};